import { PlusSquareOutlined } from '@ant-design/icons';
import { Select, SelectProps } from '@framework/ui';
import { groupBy, toPairs } from 'lodash';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { IWidgetType } from '../IWidgetType';
import { WidgetType } from '../Widget';
import { WidgetRepository } from '../repository/WidgetRepository';
import { WidgetMainTypeOptions } from './WidgetMainTypeOptions';

export interface WidgetSelectProps extends SelectProps<any> {
  width?: string;
  onChange?: (value: WidgetType) => void;
  /**
   * Filtra i widget da mostrare nella lista
   */
  filter?: (widget: IWidgetType<any>) => boolean;
  /**
   * Tipi di widget aggiuntivi da aggiungere alla lista, a prescindere dal
   * filtro passato in `filter`.
   */
  extraTypes?: WidgetType[];
}

export const CustomOptions = styled.span`
  display: flex;
  align-items: center;
  font-size: 16px;
  .anticon {
    margin-right: 8px;
    font-size: 20px;
  }
`;

const CustomOptionsDivider = styled.div`
  padding-top: 12px;
  font-weight: 400;
  font-size: 14px;
`;

// Widget esclusi dall'elenco
export function isExcludedWidgetType(type: WidgetType) {
  return [WidgetType.Question, WidgetType.Page].find(t => t == type);
}
/**
 * Select per selezionare un tipo di widget
 */
export function WidgetSelect(props: WidgetSelectProps) {
  const { width, onChange, filter, extraTypes, ...otherProps } = props;

  const options = useMemo(() => {
    const widgets = [
      // 1. Quelli filtrati
      ...WidgetRepository.listAll().filter(
        filter ?? (w => !isExcludedWidgetType(w.type)),
      ),
      // 2. Aggiungiamo gli extra a prescindere dal filtro
      ...(extraTypes?.map(t => WidgetRepository.find(t)!) ?? []),
    ];

    const widgetTypes = groupBy(widgets, t => t.category);

    return toPairs(widgetTypes)
      .sort((w1, w2) => w2[0].localeCompare(w1[0]))
      .map(([group, types]) => ({
        label: (
          <CustomOptionsDivider>
            {WidgetMainTypeOptions[group] || 'Altro'}
          </CustomOptionsDivider>
        ),
        options: types.map(w => ({
          label: (
            <CustomOptions>
              {w.icon ?? <PlusSquareOutlined />} {w.name ?? w.type}
            </CustomOptions>
          ),
          labelText: w.name ?? w.type,
          value: w.type,
        })),
      }));
  }, [filter, extraTypes]);

  return (
    <Select
      style={{ width: `${width ?? '100%'}` }}
      placeholder="Seleziona un tipo di campo per aggiungerlo"
      showSearch
      optionFilterProp="labelText"
      size="large"
      options={options}
      // Il value è null perché il componente non seleziona davvero un widget
      // ma è utilizzato unicamente come supporto grafico alla funzione di onChange
      // value={null}
      {...otherProps}
      onChange={onChange}
    />
  );
}
